/**
 * Dataset Service
 *
 * Handles API calls for dataset operations.
 * Provides access to dataset listings and real-time statistics.
 */

import { fetchWithAuth } from '../utils/api';
import type {
  DatasetListResponse,
  DatasetFilters,
  DatasetStats,
} from './types';

const DATASET_BASE_PATH = '/v1/datasets';

/**
 * List all datasets for the authenticated user's organization
 *
 * Supports filtering by dataset type and status.
 *
 * @param filters - Optional filters (type, status)
 * @returns Promise<DatasetListResponse>
 * @throws APIErrorException on error
 */
export const listDatasets = async (
  filters?: DatasetFilters
): Promise<DatasetListResponse> => {
  const params = new URLSearchParams();

  if (filters?.type) {
    params.append('type', filters.type);
  }

  if (filters?.status) {
    params.append('status', filters.status);
  }

  const queryString = params.toString();
  const endpoint = queryString
    ? `${DATASET_BASE_PATH}?${queryString}`
    : DATASET_BASE_PATH;

  return fetchWithAuth(endpoint);
};

/**
 * Get real-time statistics for a specific dataset
 *
 * Statistics are automatically updated via database triggers
 * when signals are ingested.
 *
 * @param datasetId - UUID of the dataset
 * @returns Promise<DatasetStats>
 * @throws APIErrorException on not found or server errors
 */
export const getDatasetStats = async (
  datasetId: string
): Promise<DatasetStats> => {
  return fetchWithAuth(`${DATASET_BASE_PATH}/${datasetId}/stats`);
};

/**
 * Create a new dataset
 *
 * Used for manual entry, smart extract, and bulk import flows.
 *
 * @param name - Name of the dataset
 * @param type - Dataset type (defaults to 'import')
 * @param teamId - Optional owning team (D2). Omit/undefined for a private
 *   collection visible only to the creator; pass a team you belong to to
 *   share it with that team.
 * @returns Promise<Dataset> - The created dataset
 * @throws APIErrorException on validation errors or server errors
 */
export const createDataset = async (
  name: string,
  type: string = 'import',
  teamId?: string | null
): Promise<import('./types').Dataset> => {
  const body: { name: string; type: string; team_id?: string } = { name, type };
  if (teamId) {
    body.team_id = teamId;
  }
  return fetchWithAuth(DATASET_BASE_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
};

/**
 * Dataset Service object for easier imports
 */
export const datasetService = {
  list: listDatasets,
  getStats: getDatasetStats,
  create: createDataset,
};

export default datasetService;